import { useMemo } from 'react';
import { placementLabel } from '@/lib/placementLabels';
import { X, ChevronRight, MapPin, CloudRain, Play } from 'lucide-react';

// ─── Types ──────────────────────────────────────────────────────────────────

export interface AdPreviewData {
  brandName: string;
  logoUrl: string;
  headline: string;
  body: string;
  ctaText: string;
  backgroundColor: string;
  placement: string;
  templateKey: string;
  creativeMode?: string;
  heroImageUrl?: string;
  videoUrl?: string;
}

// ─── Helpers ────────────────────────────────────────────────────────────────

function contrastText(hex: string): string {
  const c = hex.replace('#', '');
  if (c.length < 6) return '#ffffff';
  const r = parseInt(c.slice(0, 2), 16);
  const g = parseInt(c.slice(2, 4), 16);
  const b = parseInt(c.slice(4, 6), 16);
  const lum = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return lum > 0.5 ? '#1a1a1a' : '#ffffff';
}

function lighten(hex: string, amount: number): string {
  const c = hex.replace('#', '');
  if (c.length < 6) return hex;
  const r = Math.min(255, parseInt(c.slice(0, 2), 16) + amount);
  const g = Math.min(255, parseInt(c.slice(2, 4), 16) + amount);
  const b = Math.min(255, parseInt(c.slice(4, 6), 16) + amount);
  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
}

// ─── Placement-specific tag ─────────────────────────────────────────────────

function placementTag(placement: string): { label: string; style: string } {
  if (placement.includes('festival') || placement.includes('event')) {
    return { label: 'EVENT', style: 'bg-amber-500/90 text-white' };
  }
  return { label: 'SPONSORED', style: 'bg-white/20 text-white/80' };
}

// ─── Standard Ad Card (text + logo) ────────────────────────────────────────

function AdCard({ data, size = 'normal' }: { data: AdPreviewData; size?: 'normal' | 'tall' | 'full' }) {
  const fg = contrastText(data.backgroundColor || '#0f3d2e');
  const bg = data.backgroundColor || '#0f3d2e';
  const tag = placementTag(data.placement);
  const isTall = size === 'tall' || size === 'full';

  return (
    <div
      className="rounded-xl overflow-hidden shadow-lg transition-all duration-300"
      style={{ backgroundColor: bg }}
    >
      {/* Top row: tag + close */}
      <div className="flex items-center justify-between px-3 pt-2.5 pb-1">
        <span className={`text-[9px] font-semibold tracking-wider px-1.5 py-0.5 rounded-full ${tag.style}`}>
          {tag.label}
        </span>
        <X className="w-3 h-3 opacity-40" style={{ color: fg }} />
      </div>

      {/* Body */}
      <div className={`px-3 pb-3 ${isTall ? 'pt-2' : 'pt-0.5'}`}>
        <div className="flex gap-2.5 items-start">
          {/* Logo */}
          {data.logoUrl ? (
            <img
              src={data.logoUrl}
              alt=""
              className="w-9 h-9 rounded-lg object-cover flex-shrink-0 mt-0.5"
              style={{ border: `1px solid ${lighten(bg, 30)}` }}
            />
          ) : (
            <div
              className="w-9 h-9 rounded-lg flex-shrink-0 mt-0.5 flex items-center justify-center text-[11px] font-bold"
              style={{ backgroundColor: lighten(bg, 25), color: fg }}
            >
              {data.brandName?.charAt(0)?.toUpperCase() || 'S'}
            </div>
          )}

          {/* Text */}
          <div className="flex-1 min-w-0">
            <p
              className="text-[12px] font-bold leading-tight truncate"
              style={{ color: fg }}
            >
              {data.headline || 'Your headline here'}
            </p>
            <p
              className="text-[10px] leading-snug mt-0.5 line-clamp-2 opacity-80"
              style={{ color: fg }}
            >
              {data.body || 'Describe your business in a sentence or two.'}
            </p>
          </div>
        </div>

        {/* CTA */}
        {data.ctaText && (
          <div className="mt-2 flex items-center gap-1" style={{ color: fg }}>
            <span className="text-[10px] font-semibold opacity-90">{data.ctaText}</span>
            <ChevronRight className="w-3 h-3 opacity-60" />
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Full-bleed Image Card ──────────────────────────────────────────────────

function FullBleedImageCard({ data }: { data: AdPreviewData }) {
  const tag = placementTag(data.placement);

  return (
    <div className="rounded-xl overflow-hidden shadow-lg relative" style={{ minHeight: 140 }}>
      {/* Background image */}
      {data.heroImageUrl ? (
        <img
          src={data.heroImageUrl}
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
        />
      ) : (
        <div className="absolute inset-0 bg-gray-700" />
      )}

      {/* Dark gradient overlay for legibility */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-black/10" />

      {/* Content */}
      <div className="relative z-10">
        {/* Top row: tag + close */}
        <div className="flex items-center justify-between px-3 pt-2.5 pb-1">
          <span className={`text-[9px] font-semibold tracking-wider px-1.5 py-0.5 rounded-full ${tag.style}`}>
            {tag.label}
          </span>
          <X className="w-3 h-3 text-white/40" />
        </div>

        {/* Logo + CTA at bottom */}
        <div className="px-3 pb-3 pt-16">
          {data.logoUrl && (
            <img
              src={data.logoUrl}
              alt=""
              className="w-8 h-8 rounded-lg object-cover mb-2 border border-white/20"
            />
          )}
          {data.ctaText && (
            <div className="flex items-center gap-1 text-white">
              <span className="text-[11px] font-semibold">{data.ctaText}</span>
              <ChevronRight className="w-3 h-3 opacity-60" />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Video Loop Card ────────────────────────────────────────────────────────

function VideoLoopCard({ data }: { data: AdPreviewData }) {
  const tag = placementTag(data.placement);

  return (
    <div className="rounded-xl overflow-hidden shadow-lg relative" style={{ minHeight: 140 }}>
      {/* Video or placeholder */}
      {data.videoUrl ? (
        <video
          src={data.videoUrl}
          className="absolute inset-0 w-full h-full object-cover"
          muted
          autoPlay
          loop
          playsInline
        />
      ) : (
        <div className="absolute inset-0 bg-gray-700 flex items-center justify-center">
          <Play className="w-8 h-8 text-gray-500" />
        </div>
      )}

      {/* Dark gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-black/10" />

      {/* Content */}
      <div className="relative z-10">
        {/* Top row: tag + close */}
        <div className="flex items-center justify-between px-3 pt-2.5 pb-1">
          <span className={`text-[9px] font-semibold tracking-wider px-1.5 py-0.5 rounded-full ${tag.style}`}>
            {tag.label}
          </span>
          <X className="w-3 h-3 text-white/40" />
        </div>

        {/* Logo + CTA at bottom */}
        <div className="px-3 pb-3 pt-16">
          {data.logoUrl && (
            <img
              src={data.logoUrl}
              alt=""
              className="w-8 h-8 rounded-lg object-cover mb-2 border border-white/20"
            />
          )}
          {data.ctaText && (
            <div className="flex items-center gap-1 text-white">
              <span className="text-[11px] font-semibold">{data.ctaText}</span>
              <ChevronRight className="w-3 h-3 opacity-60" />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Map Pin Preview ────────────────────────────────────────────────────────

function MapPinPreview({ data }: { data: AdPreviewData }) {
  const bg = data.backgroundColor || '#0f3d2e';
  const fg = contrastText(bg);
  return (
    <div className="flex flex-col items-center">
      <div
        className="rounded-xl px-3 py-2 shadow-lg max-w-[180px] relative"
        style={{ backgroundColor: bg }}
      >
        <div className="flex items-center gap-2">
          {data.logoUrl ? (
            <img src={data.logoUrl} alt="" className="w-6 h-6 rounded-md object-cover" />
          ) : (
            <div className="w-6 h-6 rounded-md flex items-center justify-center text-[9px] font-bold" style={{ backgroundColor: lighten(bg, 25), color: fg }}>
              {data.brandName?.charAt(0)?.toUpperCase() || 'S'}
            </div>
          )}
          <div>
            <p className="text-[10px] font-bold leading-tight truncate" style={{ color: fg }}>
              {data.headline || data.brandName || 'Business'}
            </p>
            <p className="text-[8px] opacity-70 truncate" style={{ color: fg }}>
              {data.ctaText || 'Tap to learn more'}
            </p>
          </div>
        </div>
        {/* Arrow */}
        <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 w-0 h-0 border-l-[6px] border-l-transparent border-r-[6px] border-r-transparent border-t-[8px]" style={{ borderTopColor: bg }} />
      </div>
      <MapPin className="w-5 h-5 mt-1 text-red-500" />
    </div>
  );
}

// ─── Creative-mode-aware Ad Renderer ────────────────────────────────────────

function AdRenderer({ data, size = 'normal' }: { data: AdPreviewData; size?: 'normal' | 'tall' | 'full' }) {
  const mode = data.creativeMode || 'ai_generated';

  if (mode === 'client_full_bleed') {
    return <FullBleedImageCard data={data} />;
  }
  if (mode === 'client_video') {
    return <VideoLoopCard data={data} />;
  }
  // ai_generated, admin_authored, client_image all use the standard card
  return <AdCard data={data} size={size} />;
}

// ─── Phone Frame ────────────────────────────────────────────────────────────

export default function SponsorAdPreview({ data }: { data: AdPreviewData }) {
  const placement = data.placement || 'passenger_home_header';

  const isMapPin = placement === 'map_pin_featured';
  const isFestival = placement.includes('festival') || placement.includes('event');
  const isRideComplete = placement.includes('ride_complete');
  const isWeather = placement.includes('weather');
  const isMorning = placement.includes('morning') || placement.includes('commute');

  return (
    <div className="flex flex-col items-center">
      {/* Phone frame */}
      <div className="w-[280px] bg-black rounded-[2rem] p-2 shadow-2xl">
        <div className="bg-gray-900 rounded-[1.5rem] overflow-hidden relative" style={{ minHeight: 480 }}>
          {/* Status bar */}
          <div className="flex items-center justify-between px-5 pt-3 pb-1">
            <span className="text-[10px] text-white/60 font-medium">9:41</span>
            <div className="flex items-center gap-1">
              <div className="w-3.5 h-2 border border-white/40 rounded-sm relative">
                <div className="absolute inset-0.5 bg-emerald-400 rounded-[1px]" style={{ width: '70%' }} />
              </div>
            </div>
          </div>

          {/* App header */}
          <div className="px-4 pt-1 pb-2 flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg bg-emerald-600 flex items-center justify-center text-white text-[10px] font-bold">H</div>
            <span className="text-white text-[13px] font-semibold">H-Plate</span>
          </div>

          {/* Content area */}
          <div className="px-3 pb-4 space-y-2.5">
            {/* Top placement ads */}
            {(placement === 'passenger_home_header' || isMorning) && (
              <>
                <AdRenderer data={data} />
                {/* Fake content below */}
                <div className="space-y-2 pt-1">
                  <div className="h-2.5 bg-gray-800 rounded w-3/4" />
                  <div className="h-2.5 bg-gray-800 rounded w-1/2" />
                  <div className="h-20 bg-gray-800/50 rounded-lg mt-2" />
                </div>
              </>
            )}

            {/* Festival takeover */}
            {isFestival && (
              <>
                <AdRenderer data={data} size="tall" />
                <div className="space-y-2 pt-1">
                  <div className="h-2.5 bg-gray-800 rounded w-2/3" />
                  <div className="h-16 bg-gray-800/50 rounded-lg mt-1" />
                </div>
              </>
            )}

            {/* Weather alert banner */}
            {isWeather && (
              <>
                <div className="flex items-center gap-2 bg-gray-800/60 rounded-lg px-3 py-2 mb-1">
                  <CloudRain className="w-4 h-4 text-blue-400" />
                  <span className="text-[10px] text-gray-300">Rain expected this afternoon</span>
                </div>
                <AdRenderer data={data} />
                <div className="space-y-2 pt-1">
                  <div className="h-2.5 bg-gray-800 rounded w-1/2" />
                  <div className="h-16 bg-gray-800/50 rounded-lg" />
                </div>
              </>
            )}

            {/* Ride complete modal */}
            {isRideComplete && (
              <>
                <div className="space-y-2 pt-2">
                  <div className="h-2.5 bg-gray-800 rounded w-3/4" />
                  <div className="h-2.5 bg-gray-800 rounded w-1/2" />
                </div>
                <div className="mt-4 bg-gray-800/40 rounded-xl p-3 border border-gray-700/50">
                  <div className="text-center mb-3">
                    <p className="text-[11px] text-emerald-400 font-semibold">Ride Complete</p>
                    <p className="text-[18px] text-white font-bold mt-0.5">EC$5.00</p>
                    <p className="text-[9px] text-gray-400 mt-0.5">Roseau → Canefield</p>
                  </div>
                  <div className="border-t border-gray-700/50 pt-2.5">
                    <AdRenderer data={data} />
                  </div>
                </div>
              </>
            )}

            {/* Map pin */}
            {isMapPin && (
              <>
                {/* Fake map */}
                <div className="relative h-[280px] bg-gray-800/30 rounded-lg overflow-hidden">
                  <div className="absolute inset-0 opacity-20">
                    {/* Grid lines to simulate map */}
                    {Array.from({ length: 8 }).map((_, i) => (
                      <div key={`h${i}`} className="absolute w-full h-px bg-gray-600" style={{ top: `${(i + 1) * 12}%` }} />
                    ))}
                    {Array.from({ length: 6 }).map((_, i) => (
                      <div key={`v${i}`} className="absolute h-full w-px bg-gray-600" style={{ left: `${(i + 1) * 16}%` }} />
                    ))}
                  </div>
                  <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
                    <MapPinPreview data={data} />
                  </div>
                </div>
              </>
            )}

            {/* Default fallback */}
            {!['passenger_home_header', 'map_pin_featured'].includes(placement) && !isFestival && !isRideComplete && !isWeather && !isMorning && (
              <>
                <AdRenderer data={data} />
                <div className="space-y-2 pt-1">
                  <div className="h-2.5 bg-gray-800 rounded w-3/4" />
                  <div className="h-2.5 bg-gray-800 rounded w-1/2" />
                </div>
              </>
            )}
          </div>

          {/* Bottom nav bar */}
          <div className="absolute bottom-0 left-0 right-0 bg-gray-900 border-t border-gray-800 px-4 py-2 flex justify-around">
            {['Home', 'Rides', 'Map', 'Profile'].map((tab) => (
              <div key={tab} className="flex flex-col items-center gap-0.5">
                <div className="w-4 h-4 rounded bg-gray-800" />
                <span className="text-[8px] text-gray-500">{tab}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Placement label */}
      <p className="text-[11px] text-gray-500 mt-3 text-center">
        {placementLabel(placement)}
      </p>
    </div>
  );
}

